import { type FieldMetadata, getInputProps } from '@conform-to/react';
import clsx from 'clsx';
import { type ReactNode } from 'react';
import { CheckboxField } from '../catalyst-ui/checkbox';
import { ErrorMessage, Label } from '../catalyst-ui/fieldset';
import { Explanation } from '../Explanation';

type Props = {
    inputClassName?: string;
    field: FieldMetadata<any, Record<string, any>>;
    fieldClassName?: string;
    label: ReactNode | string;
    labelClassName?: string;
    shortExplanation?: ReactNode | string;
    shortExplanationClassName?: string;
    longExplanation?: ReactNode | string;
    // Controlled mode props
    checked?: boolean;
    onChange?: (checked: boolean) => void;
};

export function FormCheckboxField({
    field,
    fieldClassName,
    inputClassName,
    label,
    labelClassName,
    shortExplanation,
    shortExplanationClassName,
    longExplanation,
    checked,
    onChange,
}: Props) {
    const { key, ...fieldInputProps } = getInputProps(field, { type: 'checkbox' });

    // Support controlled mode when checked/onChange are provided
    const controlledProps = checked !== undefined
        ? {
            checked,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.checked)
        }
        : {};

    return (
        <CheckboxField className={fieldClassName}>
            <input key={key} {...fieldInputProps} {...controlledProps} className={inputClassName} />
            <Label className={clsx('block text-sm font-medium', labelClassName)} htmlFor={fieldInputProps.id}>
                {label}
            </Label>
            <div className='col-span-2'>
                {shortExplanation || longExplanation
                    ? (
                        <Explanation
                            short={shortExplanation}
                            long={longExplanation}
                            shortClassName={shortExplanationClassName}
                        />
                    )
                    : null}
                {field.valid ? null : field.errors?.map(x => <ErrorMessage key={x}>{x}</ErrorMessage>)}
            </div>
        </CheckboxField>
    );
}
